import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTablePagination, useAppMeta } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useLazyGetBranchCOCVendorSummaryQuery } from "store/services/reportApi";
import { Visibility, Search, InfoOutlined } from "@mui/icons-material";

const BranchCOCVendorSummary = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();
    const branchId = searchParams.get("branch");
    const branchName = searchParams.get("branchName") || "Branch";
    const pmProvinceId = searchParams.get("pmProvinceId");
    const pmProvinceName = searchParams.get("pmProvinceName") || "";
    const [sortBy, setSortBy] = useState("shopName:1");
    const [keyword, setKeyword] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedKeyword(keyword), 400);
        return () => clearTimeout(timer);
    }, [keyword]);

    const provinceListPath = `/app/crm/reports/coc-branch-report?mode=provinces&pmLevel=branches&pmProvinceId=${pmProvinceId || ""}&pmProvinceName=${encodeURIComponent(pmProvinceName)}`;

    const breadcrumbs = [
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "Pending Branch COC Report", path: "/app/crm/reports/coc-branch-report" },
        ...(pmProvinceId
            ? [{ title: pmProvinceName || "Province", path: provinceListPath }]
            : []),
        { title: `${branchName} — By Vendor` },
    ];

    const [fetchSummary, { data: summaryData, isLoading, isFetching, error: summaryError }] =
        useLazyGetBranchCOCVendorSummaryQuery();

    const fetchData = useCallback(() => {
        if (!branchId) {
            setErrorMessage("No branch selected.");
            return;
        }
        const [field, direction] = sortBy.split(":");
        fetchSummary({
            branchId,
            params: {
                page: currentPage,
                limit: perPage,
                sortedBy: field,
                sortDirection: direction,
                keyword: debouncedKeyword?.trim() || undefined,
            },
        }).catch((err) => {
            setErrorMessage(err?.data?.message || "Failed to fetch vendor COC summary");
        });
    }, [branchId, fetchSummary, currentPage, perPage, sortBy, debouncedKeyword]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (summaryError) setErrorMessage("Failed to fetch vendor COC summary.");
    }, [summaryError]);

    const columns = [
        {
            name: "Vendor",
            selector: (row) => row.shopName,
            cell: (row) => (
                <Box>
                    <Typography
                        variant="body2"
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                        }}
                    >
                        {row.shopName || "N/A"}
                        {row.responsibleType === "User" && (
                            <Tooltip
                                arrow
                                title={`Instead of a vendor, the actual responsible is "${
                                    row.ownerName || row.shopName
                                }" — a user`}
                            >
                                <InfoOutlined
                                    fontSize="small"
                                    color="action"
                                    sx={{ cursor: "help" }}
                                />
                            </Tooltip>
                        )}
                    </Typography>
                    {row.ownerName && (
                        <Typography variant="caption" color="text.secondary">
                            {row.ownerName}
                        </Typography>
                    )}
                </Box>
            ),
            grow: 1.5,
        },
        {
            name: "Not Paid",
            selector: (row) => row.totalNotPaid,
            cell: (row) => `Rs. ${row.totalNotPaid?.toFixed(2) || "0.00"}`,
        },
        {
            name: "Action",
            cell: (row) => (
                <Tooltip title="View Shipments">
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => {
                            const params = new URLSearchParams();
                            params.set("branch", branchId);
                            params.set("branchName", branchName);
                            params.set("vendorId", row._id);
                            params.set("vendorName", row.shopName || "");
                            if (pmProvinceId) {
                                params.set("pmProvinceId", pmProvinceId);
                                params.set("pmProvinceName", pmProvinceName);
                            }
                            navigate(`/app/crm/reports/branch-coc-vendor-detail?${params.toString()}`);
                        }}
                    >
                        <Visibility />
                    </IconButton>
                </Tooltip>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "100px",
        },
    ];

    useAppMeta(`COC by Vendor — ${branchName}`);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Error error={summaryError} />
            <BreadcrumbLayout.Paper>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        gap: 2,
                        alignItems: "center",
                        px: 2,
                        pt: 2,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        {branchName} — Pending COC by Vendor
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center",mb:1 }}>
                        <TextField
                            size="small"
                            placeholder="Search vendor name..."
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            sx={{ minWidth: 220 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <BaseSelect
                            label="Sort By"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            options={[
                                { label: "Name (Asc)", value: "shopName:1" },
                                { label: "Name (Desc)", value: "shopName:-1" },
                                { label: "Not Paid (Asc)", value: "totalNotPaid:1" },
                                { label: "Not Paid (Desc)", value: "totalNotPaid:-1" },
                            ]}
                            getOptionLabel={(opt) => opt.label}
                            getOptionValue={(opt) => opt.value}
                            size="small"
                            sx={{ minWidth: 220 }}
                        />
                    </Box>
                </Box>
                {summaryData?.totalNotPaid !== undefined && (
                    <Box sx={{ px: 2, pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Not Paid: <strong>Rs. {Number(summaryData.totalNotPaid || 0).toFixed(2)}</strong>
                        </Typography>
                    </Box>
                )}
                {isLoading || isFetching ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : summaryData?.data?.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No pending COC found for any vendor at this branch.
                        </Typography>
                    </Box>
                ) : (
                    <AppTable
                        columns={columns}
                        data={summaryData?.data || []}
                        progressPending={isLoading || isFetching}
                        paginationServer
                        paginationPerPage={perPage}
                        paginationTotalRows={summaryData?.totalItems ?? 0}
                        paginationDefaultPage={currentPage}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                    />
                )}
                <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={() => setErrorMessage("")}>
                    <Alert severity="error" onClose={() => setErrorMessage("")}>
                        {errorMessage}
                    </Alert>
                </Snackbar>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default BranchCOCVendorSummary;
